import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";
import { projectKeys } from "@/lib/query";

export type ProjectLifecyclePhase =
  | "pre_construction"
  | "design"
  | "construction"
  | "post_construction"
  | "completed";

interface Project {
  id: string;
  name: string;
  status: string;
  type: string;
  phase: ProjectLifecyclePhase;
  phaseChangedAt: string | null;
  phaseChangedByUserId: string | null;
  projectNumber: string | null;
  deliveryType: string | null;
  location: string | null;
  client: string | null;
  superintendent: string | null;
  projectManager: string | null;
  startDate: string | null;
  endDate: string | null;
  contractValue: string | null;
  percentComplete: string | null;
  description: string | null;
  imageUrl: string | null;
  clientCompanyId: string | null;
  generalContractorCompanyId: string | null;
  architectCompanyId: string | null;
  architectProjectNumber: string | null;
  contractDate: string | null;
  contractFor: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

export type { Project };

export function useProjects() {
  return useQuery({
    queryKey: projectKeys.all,
    queryFn: () => apiFetch<Project[]>("/projects"),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => apiFetch<Project>(`/projects/${id}`),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Project>) => apiPost<{ data: Project }>("/projects", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.all });
      toast.success("Project created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<Project>) =>
      apiPatch<{ data: Project }>(`/projects/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.all });
      toast.success("Project updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/projects/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.all });
      toast.success("Project archived");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export const PROJECT_PHASE_LABELS: Record<ProjectLifecyclePhase, string> = {
  pre_construction: "Pre-Construction",
  design: "Design",
  construction: "Construction",
  post_construction: "Post-Construction",
  completed: "Completed",
};

export const PROJECT_PHASE_ORDER: ProjectLifecyclePhase[] = [
  "pre_construction",
  "design",
  "construction",
  "post_construction",
  "completed",
];

export function nextProjectPhase(current: ProjectLifecyclePhase): ProjectLifecyclePhase | null {
  const idx = PROJECT_PHASE_ORDER.indexOf(current);
  return idx >= 0 && idx < PROJECT_PHASE_ORDER.length - 1
    ? (PROJECT_PHASE_ORDER[idx + 1] ?? null)
    : null;
}

export function useAdvanceProjectPhase(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phase: ProjectLifecyclePhase) =>
      apiPost<{ data: Project }>(`/projects/${projectId}/phase`, { phase }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: projectKeys.all });
      qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      toast.success(`Advanced to ${PROJECT_PHASE_LABELS[res.data.phase]}`);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
